import type { TOC } from '@ember/component/template-only';
import {
  PrismButton,
  PrismInput,
  PrismLoadingBar,
  PrismModal,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import ParityParityManagerMailTemplateRenderer from 'lumen/components/parity/parity-manager/mail-template-renderer';
import type MarketParityParityManagerMailController from 'lumen/controllers/market/parity/parity-manager/mail';
import {
  BCC_RECEIVERS_LIMIT,
  CC_RECEIVERS_LIMIT,
  MAX_LENGTH_FREE_TEXT,
  RECEIVERS_LIMIT,
} from 'lumen/controllers/market/parity/parity-manager/mail';

import styles from './mail.module.css';

interface MarketParityParityManagerMailSignature {
  Args: {
    controller: MarketParityParityManagerMailController;
    model: unknown;
  };
}

<template>
  <PrismModal
    @onClose={{@controller.redirectOnClose}}
    @title={{t "parity_manager.violation_mail_modal.title"}}
  >
    <:default>
      <div>
        {{#if @controller.model.isLoading}}
          <div class="relative">
            <PrismLoadingBar />
            <div
              class="modal--stretched
                {{styles.parity-manager-email-violation-modal}}"
            >
            </div>
          </div>
        {{else}}
          <div
            class="modal--stretched modal--padded
              {{styles.parity-manager-email-violation-modal}}"
          >
            {{#if @controller.isCopyTextFlow}}
              <div class="pm-email-copy-container flex flex-col">
                <PrismInput
                  @readonly={{true}}
                  @rows={{25}}
                  @textarea={{true}}
                  @value={{@controller.templateStringContent}}
                  id="email-textarea"
                />
                <div class="align-center flex justify-center p-500">
                  <PrismButton
                    @label={{t "parity_manager.violation_mail_modal.copy_text"}}
                    @onClick={{@controller.copyText}}
                  />
                </div>
              </div>
            {{else}}
              <div
                class="h-full gap-400
                  {{styles.parity-manager-email-violation-modal-container}}"
              >
                <div>
                  <div class="mt-400 flex flex-col">
                    <PrismInput
                      @errorMessage={{@controller.receiversErrorMessage}}
                      @hasErrors={{@controller.hasReceiversError}}
                      @helpText={{t
                        "parity_manager.violation_mail_receivers_info.count"
                        count=RECEIVERS_LIMIT
                      }}
                      @label={{t "general.to"}}
                      @onInput={{@controller.updateReceivers}}
                      @required={{true}}
                      @requiredErrorMessage={{t "general.required"}}
                      @value={{@controller.receivers}}
                    />
                    <PrismInput
                      @errorMessage={{@controller.ccReceiversErrorMessage}}
                      @hasErrors={{@controller.hasCCReceiversError}}
                      @helpText={{t
                        "parity_manager.violation_mail_receivers_info.count"
                        count=CC_RECEIVERS_LIMIT
                      }}
                      @label={{t "general.cc"}}
                      @onInput={{@controller.updateCcReceivers}}
                      @value={{@controller.ccReceivers}}
                    />
                    <PrismInput
                      @errorMessage={{@controller.bccReceiversErrorMessage}}
                      @hasErrors={{@controller.hasBCCReceiversError}}
                      @helpText={{t
                        "parity_manager.violation_mail_receivers_info.count"
                        count=BCC_RECEIVERS_LIMIT
                      }}
                      @label={{t "general.bcc"}}
                      @onInput={{@controller.updateBccReceivers}}
                      @value={{@controller.bccReceivers}}
                    />
                    <PrismInput
                      @label={{t
                        "parity_manager.violation_mail_modal.subject_placeholder"
                      }}
                      @onInput={{@controller.updateMailSubject}}
                      @required={{true}}
                      @requiredErrorMessage={{t "general.required"}}
                      @value={{@controller.mailSubject}}
                    />
                    <div class="flex flex-col gap-100 pb-500">
                      <p class="text-200--bold">
                        {{t
                          "parity_manager.violation_mail_modal.select_email_template"
                        }}
                      </p>
                      <PrismSelectInput
                        @disabled={{eq @controller.sortedTemplates.length 1}}
                        @isSecondary={{true}}
                        @onChange={{@controller.onTemplateChange}}
                        @options={{@controller.templateOptions}}
                        @unsafe_matchTriggerWidth={{true}}
                        @value={{@controller.selectedTemplate}}
                      />
                    </div>

                    {{#if @controller.selectedTemplate.isFreeTextAllowed}}
                      <PrismInput
                        @errorMessage={{t
                          "validation.max_length"
                          maxLength=MAX_LENGTH_FREE_TEXT
                        }}
                        @hasErrors={{@controller.hasFreeTextError}}
                        @label={{t
                          "parity_manager.violation_mail_modal.free_text_placeholder"
                        }}
                        @onInput={{@controller.updateFreeText}}
                        @rows={{5}}
                        @textarea={{true}}
                        @value={{@controller.freeText}}
                      />
                    {{/if}}
                  </div>
                </div>
                {{#if @controller.templateStringContent}}
                  <div
                    class={{local
                      styles
                      "mail-content-wrapper"
                      (if
                        @controller.selectedTemplate.isFreeTextAllowed
                        "has-free-text-input"
                      )
                    }}
                  >
                    <ParityParityManagerMailTemplateRenderer
                      @htmlContent={{@controller.templateStringContent}}
                      @isLoading={{false}}
                    />
                  </div>
                {{/if}}
              </div>
            {{/if}}
          </div>
        {{/if}}
      </div>
    </:default>
    <:footer>
      <div class="mr-500 flex h-full items-center justify-end">
        <PrismButton
          @isDisabled={{@controller.isSendButtonDisabled}}
          @label={{t "general.send"}}
          @onClick={{@controller.sendMail}}
        />
      </div>
    </:footer>
  </PrismModal>
</template> satisfies TOC<MarketParityParityManagerMailSignature>;
